import { escapeHtml, showToast, authenticatedFetch, timeAgo as utilsTimeAgo, USER_KEY } from '../shared/utils.ts'
import { trackArticleRead } from '../shared/analytics.ts'
import { isAuthenticated } from '../shared/auth.ts'
import { t, getDocumentLanguage } from '../shared/i18n.ts'
import { showConfirm } from '../shared/custom-dialog.ts'
import { api_v1_article_comments_path } from '../shared/api-routes'
import { localized_blog_path } from '../shared/routes'

function isArticlePage() {
  // Article URLs are localized: `/en/blog/<slug>`, `/uk/blog/<slug>`, … The
  // unprefixed `/blog/<slug>` form is the legacy alias still served via
  // FrontendController. Match either by anchoring `/blog/` anywhere followed
  // by a non-empty slug.
  return /\/blog\/[^/]+$/.test(window.location.pathname)
}

function initBlogListing() {
  // Blog listing is SSR — JS only handles filter navigation
  const langFilter = document.getElementById('langFilter')
  const catFilter = document.getElementById('catFilter')

  function navigate() {
    const params = new URLSearchParams()
    if ((langFilter as HTMLSelectElement)?.value) params.set('lang', (langFilter as HTMLSelectElement).value)
    if ((catFilter as HTMLSelectElement)?.value) params.set('category', (catFilter as HTMLSelectElement).value)
    const qs = params.toString()
    const base = localized_blog_path(getDocumentLanguage())
    window.location.href = `${base}${qs ? '?' + qs : ''}`
  }

  langFilter?.addEventListener('change', navigate)
  catFilter?.addEventListener('change', navigate)
}

async function initArticlePage() {
  // Article content is SSR — JS only loads comments + wires share panel
  const container = document.getElementById('articleContainer')
  if (!container) return

  initShareCopyButton()
  trackArticleRead(container.dataset.articleSlug || '', container.dataset.articleCategory || '')

  const articleId = container.dataset.articleId
  if (articleId) {
    loadArticleComments({ articleId: parseInt(articleId, 10), container })
  }
}

function initShareCopyButton() {
  const btn = document.querySelector<HTMLButtonElement>('.bl-art-share-copy')
  if (!btn) return
  btn.addEventListener('click', async () => {
    const url = btn.dataset.copyUrl || window.location.href
    try {
      await navigator.clipboard.writeText(url)
      showToast(t('blog.share.copied') || 'Link copied', 'success')
    } catch {
      showToast(t('blog.share.copyError') || 'Could not copy', 'error')
    }
  })
}

// ── Blog comments ────────────────────────────────────────────────

export async function loadArticleComments({ articleId, container }: any) {
  const apiBase = api_v1_article_comments_path(articleId)
  // The thread is paginated (20 a page by default, 50 at most) and only the
  // first page used to render, with no way to reach older comments. Walk
  // every page.
  const comments: any[] = []
  try {
    for (let page = 1; ; page++) {
      const resp = await authenticatedFetch(`${apiBase}?page=${page}&per_page=50`)
      if (!resp.ok) {
        if (page === 1) return
        break
      }
      const data = await resp.json()
      comments.push(...(data.comments || []))
      if (page >= (data.pagination?.total_pages ?? 1)) break
    }
  } catch (err) {
    console.error('Failed to load article comments:', err)
    return
  }

  renderBlogComments({ comments, articleId, container, apiBase })
}

function renderBlogComments({ comments, articleId, container, apiBase }: any) {
  let section = container.querySelector('.comments-section')
  if (section) section.remove()

  section = document.createElement('div')
  section.className = 'comments-section'

  const loggedIn = isAuthenticated()
  const formHtml = loggedIn
    ? `<div class="comment-form">
        <textarea class="comment-input" placeholder="${escapeHtml(t('comments.placeholder'))}" maxlength="2000"></textarea>
        <button class="btn-primary comment-submit">${escapeHtml(t('comments.submit'))}</button>
      </div>`
    : `<p class="comment-login-hint">${escapeHtml(t('comments.loginToComment'))}</p>`

  const commentsHtml = comments.length > 0
    ? comments.map((c: any) => renderBlogComment({ comment: c, articleId })).join('')
    : `<p class="no-comments">${escapeHtml(t('comments.noComments'))}</p>`

  section.innerHTML =
    `<h4 class="comments-title">${escapeHtml(t('comments.title'))}</h4>` +
    formHtml +
    `<div class="comments-list">${commentsHtml}</div>`

  container.appendChild(section)

  // Form submit
  const submitBtn = section.querySelector('.comment-submit')
  const textarea = section.querySelector('.comment-input')
  if (submitBtn && textarea) {
    submitBtn.addEventListener('click', async () => {
      const body = textarea.value.trim()
      if (!body) return
      // Held until the POST answers: a double click published the comment twice.
      submitBtn.disabled = true
      try {
        const resp = await authenticatedFetch(apiBase, { method: 'POST', body: { body } })
        if (resp.ok) {
          textarea.value = ''
          loadArticleComments({ articleId, container })
        } else {
          const err = await resp.json().catch(() => ({}))
          showToast(err.error || t('comments.submitError'), 'error')
        }
      } catch {
        showToast(t('comments.submitError'), 'error')
      } finally {
        submitBtn.disabled = false
      }
    })
  }

  // Reply and delete delegation
  section.addEventListener('click', async (e: MouseEvent) => {
    const replyBtn = (e.target as HTMLElement).closest('.comment-reply-btn') as HTMLElement | null
    if (replyBtn) {
      const commentId = replyBtn.dataset.commentId
      const commentItem = replyBtn.closest('.comment-item')
      if (!commentItem || commentItem.querySelector('.reply-form')) return

      const form = document.createElement('div')
      form.className = 'reply-form'
      form.innerHTML =
        `<textarea class="comment-input reply-input" placeholder="${escapeHtml(t('comments.placeholder'))}" maxlength="2000"></textarea>` +
        `<button class="btn-primary reply-submit">${escapeHtml(t('comments.submit'))}</button>`
      commentItem!.appendChild(form)

      const replySubmit = form.querySelector('.reply-submit') as HTMLButtonElement
      replySubmit.addEventListener('click', async () => {
        const body = (form.querySelector('.reply-input') as HTMLInputElement).value.trim()
        if (!body) return
        replySubmit.disabled = true
        try {
          const resp = await authenticatedFetch(apiBase, { method: 'POST', body: { body, parent_id: parseInt(commentId ?? '0') } })
          if (resp.ok) loadArticleComments({ articleId, container })
        } catch (err) {
          console.error('Failed to reply to comment:', err)
        } finally {
          replySubmit.disabled = false
        }
      })
      return
    }

    const deleteBtn = (e.target as HTMLElement).closest('.comment-delete') as HTMLElement | null
    if (deleteBtn) {
      if (!await showConfirm(t('comments.deleteConfirm'))) return
      const commentId = deleteBtn.dataset.commentId
      try {
        const resp = await authenticatedFetch(`${apiBase}/${commentId}`, { method: 'DELETE' })
        if (resp.ok) loadArticleComments({ articleId, container })
      } catch (err) {
        console.error('Failed to delete comment:', err)
      }
    }
  })
}

function renderBlogComment({ comment, articleId }: any) {
  const loggedIn = isAuthenticated()
  const currentUser = loggedIn ? JSON.parse(localStorage.getItem(USER_KEY) || '{}') : {}
  const isOwn = currentUser.id && currentUser.id === comment.user_id

  const deleteBtn = isOwn
    ? `<button class="comment-delete" data-comment-id="${comment.id}">${escapeHtml(t('comments.delete'))}</button>`
    : ''
  const replyBtn = !comment.parent_id && loggedIn
    ? `<button class="comment-reply-btn" data-comment-id="${comment.id}">${escapeHtml(t('comments.reply'))}</button>`
    : ''

  const repliesHtml = (comment.replies || [])
    .map((r: any) => renderBlogComment({ comment: r, articleId }))
    .join('')

  return `<div class="comment-item ${comment.parent_id ? 'comment-reply' : ''}" data-id="${comment.id}">
    <div class="comment-header">
      <strong>${escapeHtml(comment.user_name)}</strong>
      <span class="comment-time">${utilsTimeAgo(comment.created_at, t)}</span>
    </div>
    <p class="comment-body">${escapeHtml(comment.body)}</p>
    <div class="comment-actions">${replyBtn} ${deleteBtn}</div>
    ${repliesHtml ? `<div class="comment-replies">${repliesHtml}</div>` : ''}
  </div>`
}

export function initBlog() {
  if (isArticlePage()) {
    initArticlePage()
  } else {
    initBlogListing()
  }
}
